import { isEmpty } from 'lodash';
import type {
  ReactElement,
  ReactNode} from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { EventSourcePolyfill } from 'event-source-polyfill';
import { getSseEventSource } from '../helpers';
import type { ProgressContextType, ProgressDetailsObject } from './types';

export const ProgressContext = createContext<ProgressContextType | undefined>(
  undefined,
);

export const ProgressProvider: React.FC<{ children: ReactNode }> = ({
  children,
}): ReactElement => {
  const [datasetsRequiringSse, setDatasetsRequiringSse] = useState<string[]>(
    [],
  );
  const [progressDetails, setProgressDetails] = useState<ProgressDetailsObject>(
    {},
  );
  const [progressSSESource, setProgressSSESource] =
    useState<EventSourcePolyfill>();

  useEffect(() => {
    return () => progressSSESource?.close();
  }, []);

  useEffect(() => {
    if (!isEmpty(datasetsRequiringSse)) {
      // Closes previous SSE connection.
      progressSSESource?.close();

      const progressDetailsEndpoint =
        '/progress_details?is_rainbow=true&process_ids=';

      const progressEventSource = getSseEventSource(
        datasetsRequiringSse,
        progressDetailsEndpoint,
      );
      setProgressSSESource(progressEventSource);

      // Event Listeners for SSE messages.
      progressEventSource.onmessage = (message: MessageEvent<string>) => {
        const sseMessages = JSON.parse(`${message.data.replaceAll("'", '"')}`);
        const progressDetailsObj = {};

        for (const [datasetId, progressDetails] of Object.entries<any>(
          sseMessages,
        )) {
          const progressStatus = progressDetails['progress_status'];
          const progressPercentage = parseInt(
            progressDetails['progress_percentage'],
          );
          progressDetailsObj[datasetId] = {
            progressStatus,
            progressPercentage,
          };
        }
        setProgressDetails(progressDetailsObj);
      };
      progressEventSource.onerror = () => {
        progressEventSource.close();
      };
    }
    return () => progressSSESource?.close();
  }, [datasetsRequiringSse]); // TODO: make it optimized.

  const removeDatasetFromProgressTracking = (datasetIdToRemove: string) => {
    setDatasetsRequiringSse((prev) =>
      prev.filter((prevDatasetId) => prevDatasetId !== datasetIdToRemove),
    );
  };

  const addDatasetsForProgressTracking = (datasetIds: string[]) => {
    setDatasetsRequiringSse((prev) => [...prev, ...datasetIds]);
  };

  const value: ProgressContextType = {
    progressDetails,
    removeDatasetFromProgressTracking,
    addDatasetsForProgressTracking,
  };
  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgressContext = (): ProgressContextType => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgressContext must be used within a ProgressContext');
  }
  return context;
};
